import { useEffect, useMemo, useState } from "preact/hooks";
import type { Folder, LastSync, Me } from "./api";
import { relativeTime } from "./format";
import { Icon, folderIcon } from "./icons";
import { useOfflineStatus } from "./offline";
import { useDrafts } from "./outgoing";
import { type FolderNode, ancestors, errorBelow, folderTree, unreadBelow } from "./tree";

interface Props {
  folders: Folder[];
  current: string | null;
  lastSync: LastSync | null;
  me: Me | null;
  /** The forwarding rules are on screen. */
  rulesOpen: boolean;
  /** The offline settings are on screen. */
  settingsOpen: boolean;
  /** The notification settings are on screen. */
  notificationsOpen: boolean;
  /** The outbox is on screen. */
  outboxOpen: boolean;
  /** The drafts on the device are on screen. */
  draftsOpen: boolean;
  /** The starred mail is on screen. */
  starredOpen: boolean;
  /** Mail and actions waiting in the outbox. */
  waiting: number;
  /** The folders kept on the device (lower case). */
  offline: Set<string>;
  online: boolean;
  onSelect: (folder: string) => void;
  onStarred: () => void;
  onRules: () => void;
  onSettings: () => void;
  onNotifications: () => void;
  onOutbox: () => void;
  onDrafts: () => void;
  onSignOut: (() => void) | null;
}

/** The parents left open, kept on this device only (private windows may refuse storage). */
const OPEN_KEY = "folders-open";

function loadOpen(): Set<string> {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(OPEN_KEY) ?? "[]");
    return new Set(Array.isArray(saved) ? saved.filter((p): p is string => typeof p === "string") : []);
  } catch {
    return new Set();
  }
}

function saveOpen(open: Set<string>) {
  try {
    localStorage.setItem(OPEN_KEY, JSON.stringify([...open]));
  } catch {
    // Not kept: the parents start closed next time.
  }
}

/** Subfolders sit under their parent, which opens and closes with the arrow on its right. A parent without mail of
 * its own (Yahoo's "Y2000-2009" above "Y2000-2009/Y2003") opens and closes on a tap as well: there is nothing to
 * read in it. A closed parent shows the unread count and sync error of everything below it. Folders kept on the
 * device carry a mark; without a connection the others are dimmed (they cannot be read). */
export function FolderList({
  folders, current, lastSync, me, rulesOpen, settingsOpen, notificationsOpen, outboxOpen, draftsOpen, starredOpen, waiting, offline, online, onSelect,
  onStarred, onRules, onSettings, onNotifications, onOutbox, onDrafts, onSignOut,
}: Props) {
  const tree = useMemo(() => folderTree(folders), [folders]);
  const [open, setOpen] = useState(loadOpen);
  const status = useOfflineStatus();
  const drafts = useDrafts().length;

  const update = (change: (next: Set<string>) => void) =>
    setOpen((prev) => {
      const next = new Set(prev);
      change(next);
      saveOpen(next);
      return next;
    });
  const toggle = (path: string) => update((next) => (next.has(path) ? next.delete(path) : next.add(path)));

  // The folder on screen (from a link, a reload or a search) is never hidden inside a closed parent.
  useEffect(() => {
    const closed = current ? ancestors(current).filter((p) => !open.has(p)) : [];
    if (closed.length) update((next) => closed.forEach((p) => next.add(p)));
  }, [current]);

  const rows = (nodes: FolderNode[]) =>
    nodes.map((n) => {
      const f = n.folder;
      const parent = n.children.length > 0;
      const expanded = parent && open.has(n.path);
      const unread = expanded ? (f?.unread ?? 0) : unreadBelow(n);
      const error = expanded ? (f?.last_error ?? null) : errorBelow(n);
      const opens = f !== undefined && (!parent || f.messages > 0);
      const kept = offline.has(n.path.toLowerCase());
      return (
        <li key={n.path}>
          <div class={`folder ${n.path === current ? "selected" : ""} ${!online && opens && !kept ? "unavailable" : ""}`} style={{ "--depth": n.depth }}>
            <button
              class="folder-open"
              onClick={() => (opens ? onSelect(n.path) : toggle(n.path))}
              aria-expanded={opens || !parent ? undefined : expanded}
              title={error ?? (n.depth > 0 ? n.path : undefined)}
            >
              <Icon name={folderIcon(n.path)} size={18} />
              <span class="folder-name">{n.label}</span>
              {kept && (
                <span class="kept">
                  <Icon name="download" size={14} title="Kept on this device" />
                </span>
              )}
              {error && <span class="tag warn">!</span>}
              {unread > 0 && <span class="count">{unread.toLocaleString()}</span>}
            </button>
            {parent && (
              <button class="twisty" onClick={() => toggle(n.path)} aria-expanded={expanded} aria-label={`Subfolders of ${n.label}`}>
                <Icon name="chevron" size={16} />
              </button>
            )}
          </div>
          {expanded && <ul role="list">{rows(n.children)}</ul>}
        </li>
      );
    });

  return (
    <nav class="folders" aria-label="Folders">
      <div class="folders-head">
        <strong>{me?.yahoo_user ?? "Yahoo Mail"}</strong>
      </div>
      <ul role="list">{rows(tree)}</ul>
      <div class="folders-extra">
        <div class={`folder ${starredOpen ? "selected" : ""}`}>
          <button class="folder-open" onClick={onStarred}>
            <Icon name="star" size={18} />
            <span class="folder-name">Starred</span>
          </button>
        </div>
        {(waiting > 0 || outboxOpen) && (
          <div class={`folder ${outboxOpen ? "selected" : ""}`}>
            <button class="folder-open" onClick={onOutbox}>
              <Icon name="sent" size={18} />
              <span class="folder-name">Outbox</span>
              {waiting > 0 && <span class="count">{waiting.toLocaleString()}</span>}
            </button>
          </div>
        )}
        {(drafts > 0 || draftsOpen) && (
          <div class={`folder ${draftsOpen ? "selected" : ""}`}>
            <button class="folder-open" onClick={onDrafts}>
              <Icon name="compose" size={18} />
              <span class="folder-name">Drafts on this device</span>
              {drafts > 0 && <span class="count">{drafts.toLocaleString()}</span>}
            </button>
          </div>
        )}
        <div class={`folder ${rulesOpen ? "selected" : ""}`}>
          <button class="folder-open" onClick={onRules}>
            <Icon name="forward" size={18} />
            <span class="folder-name">Forwarding rules</span>
          </button>
        </div>
        <div class={`folder ${settingsOpen ? "selected" : ""}`}>
          <button class="folder-open" onClick={onSettings}>
            <Icon name="download" size={18} />
            <span class="folder-name">Offline</span>
          </button>
        </div>
        <div class={`folder ${notificationsOpen ? "selected" : ""}`}>
          <button class="folder-open" onClick={onNotifications}>
            <Icon name="bell" size={18} />
            <span class="folder-name">Notifications</span>
          </button>
        </div>
      </div>
      <div class="folders-foot">
        {lastSync && (
          <div class={lastSync.run_ok === false ? "warn-text" : "muted"} title={lastSync.run_error ?? undefined}>
            Synced {relativeTime(lastSync.synced_at)}
            {lastSync.run_ok === false && " (last run failed)"}
          </div>
        )}
        {status.syncing ? (
          <div class="muted">
            Keeping {status.syncing.folder} offline: {status.syncing.done.toLocaleString()} of {status.syncing.total.toLocaleString()}
            {status.syncing.what === "attachments" && " attachments"}
          </div>
        ) : status.error ? (
          <div class="warn-text" title={status.error}>
            Offline copy: {status.error}
          </div>
        ) : (
          status.kept > 0 && <div class="muted">{status.kept.toLocaleString()} messages kept offline</div>
        )}
        {me?.user?.upn && <div class="muted">{me.user.upn}</div>}
        {onSignOut && (
          <button class="text-button" onClick={onSignOut}>
            Sign out
          </button>
        )}
      </div>
    </nav>
  );
}
